import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Shield, 
    Users, 
    Save,
    RefreshCw,
    Check,
    ShoppingCart,
    MapPin,
    Wallet,
    ChefHat,
    Package,
    UserCog,
    Settings,
    Lock,
    Unlock
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============================================================
// GESTION DES RÔLES ET PERMISSIONS - ADMIN UNIQUEMENT
// Interface simple : Rôles à gauche, Permissions à droite
// ============================================================

// Définition des rôles
const ROLES = [
    { id: 'SERVEUR', label: 'Serveur', icon: Users, color: 'bg-green-500' },
    { id: 'DIRECTEUR_ADJOINT', label: 'Directeur Adjoint', icon: UserCog, color: 'bg-blue-500' },
    { id: 'DIRECTEUR', label: 'Directeur', icon: Shield, color: 'bg-purple-500' },
    { id: 'ADMIN', label: 'Administrateur', icon: Lock, color: 'bg-red-500' }
];

// Catégories de permissions avec leurs options
const PERMISSION_CATEGORIES = [
    {
        id: 'vente',
        label: 'Vente',
        icon: ShoppingCart,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        permissions: [
            { id: 'vente_creer', label: 'Créer une vente', description: 'Peut créer de nouvelles commandes' },
            { id: 'vente_modifier', label: 'Modifier une vente', description: 'Peut modifier les commandes existantes' },
            { id: 'vente_annuler', label: 'Annuler une vente', description: 'Peut annuler des commandes' },
            { id: 'vente_encaisser', label: 'Encaisser', description: 'Peut encaisser les paiements' }
        ]
    },
    {
        id: 'tables',
        label: 'Tables',
        icon: MapPin,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        permissions: [
            { id: 'tables_voir', label: 'Voir les tables', description: 'Accès à la vue des tables' },
            { id: 'tables_creer', label: 'Créer/Modifier tables', description: 'Peut ajouter ou modifier des tables' },
            { id: 'tables_fermer', label: 'Fermer une table', description: 'Peut clôturer une table' }
        ]
    },
    {
        id: 'caisse',
        label: 'Caisse',
        icon: Wallet,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        permissions: [
            { id: 'caisse_ouvrir', label: 'Ouvrir la caisse', description: 'Peut ouvrir une session de caisse' },
            { id: 'caisse_fermer', label: 'Fermer la caisse', description: 'Peut fermer une session de caisse' },
            { id: 'caisse_voir_montants', label: 'Voir les montants', description: 'Peut consulter les totaux et montants' }
        ]
    },
    {
        id: 'cuisine_bar',
        label: 'Cuisine / Bar',
        icon: ChefHat,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        permissions: [
            { id: 'cuisine_lecture', label: 'Lecture cuisine', description: 'Voir les commandes en cuisine' },
            { id: 'cuisine_modifier_statut', label: 'Modifier statut cuisine', description: 'Changer le statut des commandes' },
            { id: 'bar_lecture', label: 'Lecture bar', description: 'Voir les commandes au bar' },
            { id: 'bar_modifier_statut', label: 'Modifier statut bar', description: 'Changer le statut des boissons' }
        ]
    },
    {
        id: 'stock',
        label: 'Stock',
        icon: Package,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        permissions: [
            { id: 'stock_voir', label: 'Voir le stock', description: 'Consulter l\'inventaire' },
            { id: 'stock_modifier', label: 'Modifier le stock', description: 'Ajuster les quantités' },
            { id: 'stock_reapprovisionner', label: 'Réapprovisionner', description: 'Créer des commandes fournisseurs' }
        ]
    },
    {
        id: 'utilisateurs',
        label: 'Utilisateurs',
        icon: Users,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        permissions: [
            { id: 'users_ajouter', label: 'Ajouter utilisateur', description: 'Créer de nouveaux comptes' },
            { id: 'users_modifier', label: 'Modifier utilisateur', description: 'Modifier les comptes existants' },
            { id: 'users_supprimer', label: 'Supprimer utilisateur', description: 'Désactiver ou supprimer des comptes' }
        ]
    },
    {
        id: 'systeme',
        label: 'Paramètres Système',
        icon: Settings,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        permissions: [
            { id: 'systeme_parametres', label: 'Accès paramètres', description: 'Configurer le système' },
            { id: 'systeme_etablissements', label: 'Gérer établissements', description: 'Configurer les établissements' },
            { id: 'systeme_identifiants', label: 'Voir identifiants', description: 'Accès aux identifiants système' }
        ]
    }
];

// Permissions par défaut pour chaque rôle
const DEFAULT_PERMISSIONS = {
    SERVEUR: {
        vente_creer: true,
        vente_modifier: false,
        vente_annuler: false,
        vente_encaisser: true,
        tables_voir: true,
        tables_creer: false,
        tables_fermer: true,
        caisse_ouvrir: false,
        caisse_fermer: false,
        caisse_voir_montants: false,
        cuisine_lecture: true,
        cuisine_modifier_statut: false,
        bar_lecture: true,
        bar_modifier_statut: false,
        stock_voir: false,
        stock_modifier: false,
        stock_reapprovisionner: false,
        users_ajouter: false,
        users_modifier: false,
        users_supprimer: false,
        systeme_parametres: false,
        systeme_etablissements: false,
        systeme_identifiants: false
    },
    DIRECTEUR_ADJOINT: {
        vente_creer: true,
        vente_modifier: true,
        vente_annuler: true,
        vente_encaisser: true,
        tables_voir: true,
        tables_creer: true,
        tables_fermer: true,
        caisse_ouvrir: true,
        caisse_fermer: true,
        caisse_voir_montants: true,
        cuisine_lecture: true,
        cuisine_modifier_statut: true,
        bar_lecture: true,
        bar_modifier_statut: true,
        stock_voir: true,
        stock_modifier: true,
        stock_reapprovisionner: true,
        users_ajouter: false,
        users_modifier: false,
        users_supprimer: false,
        systeme_parametres: false,
        systeme_etablissements: false,
        systeme_identifiants: false
    },
    DIRECTEUR: {
        vente_creer: true,
        vente_modifier: true,
        vente_annuler: true,
        vente_encaisser: true,
        tables_voir: true,
        tables_creer: true,
        tables_fermer: true,
        caisse_ouvrir: true,
        caisse_fermer: true,
        caisse_voir_montants: true,
        cuisine_lecture: true,
        cuisine_modifier_statut: true,
        bar_lecture: true,
        bar_modifier_statut: true,
        stock_voir: true,
        stock_modifier: true,
        stock_reapprovisionner: true,
        users_ajouter: true,
        users_modifier: true,
        users_supprimer: false,
        systeme_parametres: true,
        systeme_etablissements: false,
        systeme_identifiants: false
    },
    ADMIN: {
        vente_creer: true,
        vente_modifier: true,
        vente_annuler: true,
        vente_encaisser: true,
        tables_voir: true,
        tables_creer: true,
        tables_fermer: true,
        caisse_ouvrir: true,
        caisse_fermer: true,
        caisse_voir_montants: true,
        cuisine_lecture: true,
        cuisine_modifier_statut: true,
        bar_lecture: true,
        bar_modifier_statut: true,
        stock_voir: true,
        stock_modifier: true,
        stock_reapprovisionner: true,
        users_ajouter: true,
        users_modifier: true,
        users_supprimer: true,
        systeme_parametres: true,
        systeme_etablissements: true,
        systeme_identifiants: true
    }
};

const RolesPermissionsPage = () => {
    const { api, user } = useAuth();
    const [selectedRole, setSelectedRole] = useState('SERVEUR');
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [originalPermissions, setOriginalPermissions] = useState(DEFAULT_PERMISSIONS);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Check admin access
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'admin';
    
    // Load saved permissions from backend
    const loadPermissions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api().get('/admin/role-permissions');
            if (response.data?.permissions) {
                // Merge with defaults to ensure all permissions exist
                const merged = { ...DEFAULT_PERMISSIONS };
                Object.keys(response.data.permissions).forEach(role => {
                    if (merged[role]) {
                        merged[role] = { ...merged[role], ...response.data.permissions[role] };
                    }
                });
                setPermissions(merged);
                setOriginalPermissions(merged);
            }
        } catch (error) {
            console.log('Using default permissions');
            setPermissions(DEFAULT_PERMISSIONS);
            setOriginalPermissions(DEFAULT_PERMISSIONS);
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => {
        if (isAdmin) {
            loadPermissions();
        }
    }, [isAdmin, loadPermissions]);
    
    // Check for changes
    useEffect(() => {
        const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
        setHasChanges(changed);
    }, [permissions, originalPermissions]);
    
    // Toggle a permission
    const togglePermission = (permissionId) => {
        if (selectedRole === 'ADMIN') {
            toast.error('Les permissions Admin ne peuvent pas être modifiées');
            return;
        }
        
        setPermissions(prev => ({
            ...prev,
            [selectedRole]: {
                ...prev[selectedRole],
                [permissionId]: !prev[selectedRole][permissionId]
            }
        }));
    };
    
    // Save permissions
    const savePermissions = async () => {
        setSaving(true);
        try {
            await api().post('/admin/role-permissions', { permissions });
            setOriginalPermissions({ ...permissions });
            toast.success('Permissions enregistrées avec succès');
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };
    
    // Reset to defaults
    const resetToDefaults = () => {
        if (selectedRole === 'ADMIN') {
            toast.error('Les permissions Admin ne peuvent pas être modifiées');
            return;
        }
        
        setPermissions(prev => ({
            ...prev,
            [selectedRole]: { ...DEFAULT_PERMISSIONS[selectedRole] }
        }));
        toast.info(`Permissions ${ROLES.find(r => r.id === selectedRole)?.label} réinitialisées`);
    };
    
    // Count enabled permissions for a role
    const countEnabledPermissions = (roleId) => {
        const rolePerms = permissions[roleId] || {};
        return Object.values(rolePerms).filter(v => v === true).length;
    };
    
    // Total permissions count
    const totalPermissions = PERMISSION_CATEGORIES.reduce((acc, cat) => acc + cat.permissions.length, 0);
    
    if (!isAdmin) {
        return (
            <div className="p-6">
                <Card className="max-w-md mx-auto">
                    <CardContent className="pt-6 text-center">
                        <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">Accès Restreint</h2>
                        <p className="text-muted-foreground">
                            Seul l'administrateur peut accéder à cette page.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    return (
        <div className="p-4 lg:p-6 space-y-4" data-testid="roles-permissions-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Shield className="w-7 h-7 text-primary" />
                        Gestion des Rôles et Permissions
                    </h1>
                    <p className="text-muted-foreground">
                        Contrôlez les accès selon le rôle pour éviter erreurs et abus
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={loadPermissions}
                        disabled={loading}
                    >
                        <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                        Actualiser
                    </Button>
                    <Button 
                        onClick={savePermissions}
                        disabled={!hasChanges || saving}
                        className="bg-green-600 hover:bg-green-700"
                    >
                        {saving ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer
                    </Button>
                </div>
            </div>
            
            {/* Main Content - Two columns */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Left Column - Roles List */}
                <Card className="lg:col-span-1">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Rôles
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-2">
                        <div className="space-y-2">
                            {ROLES.map(role => {
                                const isSelected = selectedRole === role.id;
                                const enabledCount = countEnabledPermissions(role.id);
                                const Icon = role.icon;
                                
                                return (
                                    <button
                                        key={role.id}
                                        onClick={() => setSelectedRole(role.id)}
                                        className={cn(
                                            "w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left",
                                            isSelected 
                                                ? "bg-primary text-primary-foreground shadow-md" 
                                                : "hover:bg-muted"
                                        )}
                                        data-testid={`role-btn-${role.id}`}
                                    >
                                        <div className={cn(
                                            "w-10 h-10 rounded-full flex items-center justify-center",
                                            isSelected ? "bg-white/20" : role.color
                                        )}>
                                            <Icon className={cn(
                                                "w-5 h-5",
                                                isSelected ? "text-white" : "text-white"
                                            )} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={cn(
                                                "font-medium truncate",
                                                isSelected && "text-white"
                                            )}>
                                                {role.label}
                                            </p>
                                            <p className={cn(
                                                "text-xs",
                                                isSelected ? "text-white/70" : "text-muted-foreground"
                                            )}>
                                                {enabledCount}/{totalPermissions} actives
                                            </p>
                                        </div>
                                        {role.id === 'ADMIN' && (
                                            <Lock className={cn(
                                                "w-4 h-4",
                                                isSelected ? "text-white/70" : "text-muted-foreground"
                                            )} />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
                
                {/* Right Column - Permissions */}
                <Card className="lg:col-span-3">
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Settings className="w-5 h-5" />
                                Permissions - {ROLES.find(r => r.id === selectedRole)?.label}
                            </CardTitle>
                            {selectedRole === 'ADMIN' && (
                                <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                                    <Lock className="w-3 h-3" />
                                    L'administrateur a tous les accès (non modifiable)
                                </p>
                            )}
                        </div>
                        {selectedRole !== 'ADMIN' && (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={resetToDefaults}
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Réinitialiser
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="h-[calc(100vh-320px)] pr-4">
                            <div className="space-y-6">
                                {PERMISSION_CATEGORIES.map(category => {
                                    const Icon = category.icon;
                                    const rolePerms = permissions[selectedRole] || {};
                                    const enabledInCategory = category.permissions.filter(p => rolePerms[p.id]).length;
                                    
                                    return (
                                        <div key={category.id} className={cn("rounded-lg p-4", category.bgColor)}>
                                            {/* Category Header */}
                                            <div className="flex items-center gap-2 mb-4">
                                                <Icon className={cn("w-5 h-5", category.color)} />
                                                <h3 className={cn("font-semibold", category.color)}>
                                                    {category.label}
                                                </h3>
                                                <Badge variant="outline" className="ml-auto">
                                                    {enabledInCategory}/{category.permissions.length}
                                                </Badge>
                                            </div>
                                            
                                            {/* Permissions Grid */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {category.permissions.map(permission => {
                                                    const isEnabled = rolePerms[permission.id] || false;
                                                    const isAdminRole = selectedRole === 'ADMIN';
                                                    
                                                    return (
                                                        <div 
                                                            key={permission.id}
                                                            className={cn(
                                                                "flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-800 shadow-sm",
                                                                isAdminRole && "opacity-75"
                                                            )}
                                                        >
                                                            <div className="flex-1 min-w-0 mr-3">
                                                                <p className="font-medium text-sm">{permission.label}</p>
                                                                <p className="text-xs text-muted-foreground truncate">
                                                                    {permission.description}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {isEnabled ? (
                                                                    <Check className="w-4 h-4 text-green-600" />
                                                                ) : (
                                                                    <span className="w-4 h-4" />
                                                                )}
                                                                <Switch
                                                                    checked={isEnabled}
                                                                    onCheckedChange={() => togglePermission(permission.id)}
                                                                    disabled={isAdminRole}
                                                                    data-testid={`switch-${selectedRole}-${permission.id}`}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
            
            {/* Unsaved Changes Warning */}
            {hasChanges && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
                    <Card className="bg-amber-50 border-amber-200 shadow-lg">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex items-center gap-2 text-amber-700">
                                <Unlock className="w-5 h-5" />
                                <span className="font-medium">Modifications non enregistrées</span>
                            </div>
                            <Button 
                                onClick={savePermissions}
                                disabled={saving}
                                className="bg-amber-600 hover:bg-amber-700"
                            >
                                {saving ? (
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                Enregistrer
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default RolesPermissionsPage;
